/**
 * Create/upgrade subscription dialog.
 */

"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@speakai/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogCloseButton,
} from "@speakai/ui/dialog";
import { Badge } from "@speakai/ui/badge";
import { useToast } from "@speakai/ui/toast";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { trackFunnel, trackSentryRevenue, trackFailure } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { SelectSource } from "./select-source";
import { AddSource } from "./add-source";
import { ConnectPaypalButton } from "./connect-paypal-button";
import { usePaymentMethods } from "@/features/subscription/payment-queries";
import { useSubscription } from "@/features/subscription/queries";
import {
  useStripePlan,
  usePaddlePlan,
  usePayAsYouGo,
} from "../mutations";
import { confirmStripeCardPayment, classifyStripeError } from "../payment/stripe";
import { usePaddle } from "../payment/paddle";
import { useQueryClient } from "@tanstack/react-query";
import { subscriptionKeys, paymentKeys, dashboardKeys } from "@/queries/keys";
import {
  SubscriptionPlanId,
  PaymentSourceType,
  type PlanTypes,
  type PlanDurations,
  type CreateSubscriptionData,
} from "../types";
import { Check, LoaderCircle } from "lucide-react";

interface CreateSubscriptionProps {
  open: boolean;
  onClose: () => void;
  plan: PlanTypes;
  duration: PlanDurations;
  onSuccess?: () => void;
  teamMembers?: number;
  offerTracking?: { pillCopyArm?: string; offerArm?: string };
}

type Step = "select-payment" | "processing" | "success";

export function CreateSubscription({
  open,
  onClose,
  plan,
  duration,
  onSuccess,
  teamMembers,
  offerTracking,
}: CreateSubscriptionProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("select-payment");
  const [selectedSource, setSelectedSource] = useState<{
    id: string;
    type: "card" | "paddle" | "balance";
  } | null>(null);
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [error, setError] = useState("");
  const userPickedRef = useRef(false);

  const stripePlan = useStripePlan();
  const paddlePlan = usePaddlePlan();
  const { openCheckoutAndWait } = usePaddle();
  const payAsYouGo = usePayAsYouGo();
  const toastApi = useToast();
  const queryClient = useQueryClient();

  const { data: stripeMethods = [] } = usePaymentMethods(PaymentSourceType.Stripe);
  const { data: paddleMethods = [] } = usePaymentMethods(PaymentSourceType.Paddle);
  const canAddCard = stripeMethods.length < 2;
  const canAddPaypal = paddleMethods.length < 1;

  // Reset only on open transition so a re-render doesn't clear an explicit pick.
  useEffect(() => {
    if (open) userPickedRef.current = false;
  }, [open]);

  useEffect(() => {
    // Fires on each dialog open; multi-fire across reopens is intentional (no ref guard).
    if (open) pushDataLayer("Subscription", "Open");
  }, [open]);

  const isPayAsYouGo =
    plan.planId === SubscriptionPlanId.Speak_Pay_As_You_Go;
  const isTrial = plan.planId === SubscriptionPlanId.Speak_Free_Trial;
  const { data: currentSubscription } = useSubscription();
  const isChangingPaidPlan =
    !!currentSubscription &&
    !currentSubscription.isExpired &&
    currentSubscription.planId !== SubscriptionPlanId.Speak_Free_Trial &&
    currentSubscription.planId !== SubscriptionPlanId.Speak_Pay_As_You_Go;

  const finalCost = plan.discountedCost;

  const handleSubscribe = useCallback(async () => {
    if (!selectedSource && !isPayAsYouGo) {
      setError(t("CREATE_SUBSCRIPTION.SELECT_PAYMENT_METHOD"));
      return;
    }

    pushDataLayer("Subscription", "PurchaseClick", undefined, undefined, { payment_method: selectedSource?.type });
    setStep("processing");
    setError("");

    try {
      const features = plan.features;

      // Guard: non-PAYG paid plans must have feature allowances. Sending zeros silently
      // would create a subscription with 0 minutes / 0 seats — Stripe charges, user gets nothing.
      // (PAYG legitimately has undefined features — no allowances to provision.)
      if (!isPayAsYouGo && !isTrial && !features) {
        const guardErr = new Error(
          `subscription-plan-missing-features: planId=${plan.planId} offerId=${plan.offerId ?? "none"}`,
        );
        Sentry.captureException(guardErr, {
          tags: { charge_type: "subscription" },
          extra: { planId: plan.planId, offerId: plan.offerId, duration: duration.id },
        });
        trackFailure("payment", "fail", {
          error_reason: "plan_missing_features",
          charge_type: "subscription",
        });
        setError(t("CREATE_SUBSCRIPTION.FAILED"));
        setStep("select-payment");
        toastApi.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
        return;
      }

      // Server reads addOnList via `.filter()` without optional-chaining, so we must send an
      // array (omitting causes a server crash). The React UI does not yet collect add-ons —
      // mirrors current Angular default for plans without add-on configuration.
      // TODO: wire premium add-on UI (PremiumAddOnIds) before launching paid add-ons.
      const planData: CreateSubscriptionData = {
        planId: plan.planId,
        durationId: duration.id,
        offerId: plan.offerId ?? "",
        sourceId: selectedSource?.id ?? "",
        totalHours: features?.transcriptionHours ?? 0,
        totalTeamMembers: teamMembers ?? features?.teamMembers ?? 0,
        totalTextNotes: features?.textNotes ?? 0,
        totalTranslationCharacters: features?.translationCharacters ?? 0,
        totalPromptCharacters: features?.chatCharacters ?? 0,
        addOnList: [],
        sourceType: PaymentSourceType.Stripe,
        totalCost: plan.totalCost,
        discountedCost: finalCost,
        coupon: { id: "", code: "", percentOff: 0, amountOff: 0, originalCost: plan.totalCost },
      };

      if (isPayAsYouGo) {
        const paygoResult = await payAsYouGo.mutateAsync();
        if (!paygoResult.success) {
          trackFailure("payment", "fail", { error_reason: paygoResult.error?.slice(0, 200) ?? "payasyougo_fail", charge_type: "subscription" });
          setError(t("CREATE_SUBSCRIPTION.FAILED"));
          setStep("select-payment");
          toastApi.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
          return;
        }
      } else if (selectedSource?.type === "card") {
        // Single call: server creates the subscription, charges the saved card,
        // and only returns `requiresAction` + `clientSecret` if 3DS/SCA is needed.
        // Mirrors the Angular flow (see angular-old/.../create-subscription.component.ts:178-203).
        const cardResult = await stripePlan.mutateAsync({
          paymentMethodId: selectedSource.id,
          planData,
        });
        if (!cardResult.success) {
          trackFailure("payment", "fail", { error_reason: cardResult.error?.slice(0, 200) ?? "stripe_plan_fail", charge_type: "subscription", payment_method: "card" });
          setError(t("CREATE_SUBSCRIPTION.FAILED"));
          setStep("select-payment");
          toastApi.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
          return;
        }

        if (cardResult.data?.requiresAction && cardResult.data.clientSecret) {
          try {
            await confirmStripeCardPayment(cardResult.data.clientSecret);
          } catch (stripeErr: unknown) {
            // card_error/validation_error messages come from Stripe's localized catalog
            // (e.g. "Your card was declined") and are safe to show verbatim; everything
            // else falls back to the generic localized message.
            const { message, isCardError, failureType } = classifyStripeError(stripeErr);
            const userMessage = isCardError
              ? message || t("CREATE_SUBSCRIPTION.FAILED")
              : t("CREATE_SUBSCRIPTION.FAILED");
            trackFailure("payment", "fail", {
              error_reason: message.slice(0, 200),
              charge_type: "subscription",
              payment_method: "card",
              failure_type: failureType,
            });
            pushDataLayer("Subscription", "PurchaseFail", undefined, undefined, { failure_type: failureType, payment_method: selectedSource?.type });
            setError(userMessage);
            setStep("select-payment");
            toastApi.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
            return;
          }
        }
      } else if (selectedSource?.type === "paddle") {
        const paddleResult = await paddlePlan.mutateAsync({
          planData: { ...planData, sourceType: PaymentSourceType.Paddle },
        });
        if (!paddleResult.success) {
          trackFailure("payment", "fail", { error_reason: paddleResult.error?.slice(0, 200) ?? "paddle_fail", charge_type: "subscription", payment_method: "paddle" });
          setError(t("CREATE_SUBSCRIPTION.FAILED"));
          setStep("select-payment");
          toastApi.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
          return;
        }

        if (paddleResult.data?.transactionId) {
          try {
            await openCheckoutAndWait(paddleResult.data.transactionId);
          } catch (paddleErr: unknown) {
            const reason = paddleErr instanceof Error ? paddleErr.message : "paddle_checkout_failed";
            const userCancelled = reason === "checkout_closed";
            trackFailure("payment", "fail", {
              error_reason: reason.slice(0, 200),
              charge_type: "subscription",
              payment_method: "paddle",
              failure_type: userCancelled ? "paddle_cancelled" : "paddle_checkout",
            });
            if (!userCancelled) {
              setError(reason || t("CREATE_SUBSCRIPTION.FAILED"));
              toastApi.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
            }
            setStep("select-payment");
            return;
          }
        }
      } else if (selectedSource?.type === "balance") {
        const balanceResult = await stripePlan.mutateAsync({
          paymentMethodId: "balance",
          planData,
        });
        if (!balanceResult.success) {
          trackFailure("payment", "fail", { error_reason: balanceResult.error?.slice(0, 200) ?? "balance_fail", charge_type: "subscription", payment_method: "balance" });
          setError(t("CREATE_SUBSCRIPTION.FAILED"));
          setStep("select-payment");
          toastApi.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
          return;
        }
      }

      // allSettled: a failed refetch must not surface as a payment error — charge already succeeded.
      // race: 30 s timeout prevents indefinite stall on slow post-payment network.
      await Promise.race([
        Promise.allSettled([
          queryClient.refetchQueries({ queryKey: subscriptionKeys.all }),
          queryClient.refetchQueries({ queryKey: paymentKeys.balance() }),
          queryClient.refetchQueries({ queryKey: dashboardKeys.all }),
        ]),
        new Promise<void>((resolve) => setTimeout(resolve, 30_000)),
      ]);

      setStep("success");
      trackSentryRevenue("subscription_charge", finalCost, { plan: plan.displayName, billing_cycle: duration.label, payment_method: selectedSource?.type });
      trackFunnel("subscription", "create", { plan: plan.displayName, billing_cycle: duration.label, is_trial: isTrial });
      // Balance-funded sends value 0: balance was already counted as GA4 revenue at top-up (speak_add_balance); Sentry above intentionally keeps the true finalCost.
      const purchaseValue = selectedSource?.type === "balance" ? 0 : finalCost;
      pushDataLayer("Subscription", "PurchaseSuccess", plan.displayName.slice(0, 100), purchaseValue, {
        plan: plan.displayName.slice(0, 100),
        billing_cycle: duration.id,
        payment_method: selectedSource?.type ?? "payasyougo",
        source: plan.offerId ? "special_offer" : "pricing",
        ...(plan.offerId ? { special_offer_id: String(plan.offerId).slice(0, 100) } : {}),
        ...(offerTracking?.pillCopyArm ? { pill_copy_arm: offerTracking.pillCopyArm.slice(0, 60) } : {}),
        ...(offerTracking?.offerArm ? { offer_arm: offerTracking.offerArm.slice(0, 60) } : {}),
      });
    } catch (err: unknown) {
      Sentry.captureException(err);
      const errorReason =
        err instanceof Error ? err.message : t("CREATE_SUBSCRIPTION.FAILED");
      trackFailure("payment", "fail", { error_reason: errorReason.slice(0, 200), charge_type: "subscription", payment_method: selectedSource?.type });
      setError(t("CREATE_SUBSCRIPTION.FAILED"));
      setStep("select-payment");
      toastApi.error(t("CREATE_SUBSCRIPTION.FAILED_TITLE"));
    }
  }, [
    openCheckoutAndWait,
    selectedSource,
    isPayAsYouGo,
    plan,
    duration,
    finalCost,
    isTrial,
    teamMembers,
    offerTracking,
    payAsYouGo,
    stripePlan,
    paddlePlan,
    queryClient,
    toastApi,
    t,
  ]);

  const handleClose = useCallback(() => {
    // Block dismissal while a charge is in flight — Esc/overlay/close during
    // processing would orphan a successful payment with no onResume fire.
    if (step === "processing") return;
    if (step === "success") {
      onSuccess?.();
    }
    setStep("select-payment");
    setSelectedSource(null);
    setError("");
    onClose();
  }, [step, onSuccess, onClose]);

  return (
    <>
      <Dialog open={open && !addSourceOpen} onClose={handleClose} aria-labelledby="create-sub-title">
        <DialogHeader>
          <div>
            <h2 id="create-sub-title" className="text-lg font-semibold">
              {step === "success"
                ? t("CREATE_SUBSCRIPTION.ACTIVE")
                : t("CREATE_SUBSCRIPTION.SUBSCRIPTION")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isChangingPaidPlan
                ? t("CREATE_SUBSCRIPTION.DESC")
                : t("CREATE_SUBSCRIPTION.DESC_NEW", "Your plan starts today and renews automatically. Cancel anytime.")}
            </p>
          </div>
          <DialogCloseButton onClose={handleClose} />
        </DialogHeader>

        <DialogBody className="space-y-5">
          {step === "success" ? (
            <div className="flex flex-col items-center gap-4 py-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
                <Check aria-hidden="true" className="h-7 w-7 text-success" />
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {t("CREATE_SUBSCRIPTION.SUCCESS_DESC")}
              </p>
              <Button onClick={handleClose}>{t("ACTION.CONTINUE")}</Button>
            </div>
          ) : step === "processing" ? (
            <div className="flex flex-col items-center gap-4 py-10">
              <LoaderCircle className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">{t("ACTION.PROCESSING")}</p>
            </div>
          ) : (
            <>
              {/* Plan summary */}
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div>
                  <p className="text-sm font-medium">{plan.displayName}</p>
                  <p className="text-xs text-muted-foreground">
                    {duration.label}
                    {duration.discountValue > 0 && (
                      <Badge variant="default" className="ml-2 text-[10px]">
                        {t("CREATE_SUBSCRIPTION.DISCOUNT", { value: duration.discountValue })}
                      </Badge>
                    )}
                  </p>
                </div>
                <p className="text-lg font-bold">
                  {finalCost > 0 ? `$${finalCost.toFixed(2)}` : t("CREATE_SUBSCRIPTION.FREE")}
                </p>
              </div>

              {/* Payment method selection (skip for PAYG) */}
              {!isPayAsYouGo && (
                <div className="flex flex-col gap-3" role="group" aria-labelledby="create-sub-method-label">
                  <p id="create-sub-method-label" className="text-sm font-medium">
                    {t("PAYMENT_SETTINGS.PAYMENT_METHOD")}
                  </p>
                  <SelectSource
                    onSelect={setSelectedSource}
                    onUserSelect={(source) => {
                      userPickedRef.current = true;
                      pushDataLayer("Subscription", "MethodSelect", undefined, undefined, { payment_method: source?.type });
                    }}
                    selectedSourceId={selectedSource?.id}
                    autoSelectDefault
                    showBalance={false}
                  />
                  {canAddCard && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => { pushDataLayer("Subscription", "CardFormShown"); setAddSourceOpen(true); }}
                    >
                      {t("CREATE_SUBSCRIPTION.ADD_CARD", "Add Card")}
                    </Button>
                  )}
                  {canAddPaypal && (
                    // onClose omitted: dialog stays open so user can submit the plan.
                    <ConnectPaypalButton
                      layout="full-width"
                      onSuccess={(newSource) => {
                        if (newSource && !userPickedRef.current) {
                          setSelectedSource(newSource);
                        }
                      }}
                    />
                  )}
                </div>
              )}

              {error && (
                <div className="flex flex-col gap-1">
                  <p role="alert" className="text-sm text-danger">{error}</p>
                  {selectedSource?.type === "card" && (
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline self-start focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
                      onClick={() => setAddSourceOpen(true)}
                    >
                      {t("CREATE_SUBSCRIPTION.ADD_DIFFERENT_CARD", "Add a different card")}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogBody>

        {step === "select-payment" && (
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
            >
              {t("ACTION.CANCEL")}
            </Button>
            <Button
              onClick={handleSubscribe}
              disabled={!isPayAsYouGo && !selectedSource}
            >
              {isPayAsYouGo
                ? t("PRICING.ACTION.PAY_AS_YOU_GO")
                : `${t("PRICING.ACTION.SUBSCRIBE_NOW")}: $${finalCost.toFixed(2)}`}
            </Button>
          </DialogFooter>
        )}
      </Dialog>

      <AddSource
        open={addSourceOpen}
        onClose={() => { setAddSourceOpen(false); setError(""); }}
        onSuccess={(newSource) => {
          trackFunnel("payment", "add_card_from_create_subscription", { trigger: "no_card_escape" });
          pushDataLayer("Subscription", "CardSaved");
          // Cards always auto-select; PayPal respects an explicit prior pick.
          if (newSource && (newSource.type === "card" || !userPickedRef.current)) {
            setSelectedSource(newSource);
          }
          setAddSourceOpen(false);
          setError("");
        }}
      />
    </>
  );
}
